import { z } from "zod/v4";
import { getPatient, historyFor, medicationsFor, noteSchema, resultIndexes, resultsFor, type Note, type Scope, type Store } from "./domain.js";
import { encounterRef, medicationRow, patientRef, resultRow, visitRow } from "./rows.js";

// What Careline returns INSTEAD of a mutation result when the host renders views (`welcome.host.views`;
// own-app views FR-6/FR-7; `tools.ts` branches on it): the proposing call writes nothing, so it returns the exact data the clinician will
// decide on, with the identity and revision the view needs to make the app-only write (`apply_note_draft`,
// `record_review`) in the same scope. A stale revision is refused here, never refreshed and retried.

/** The review fields a chart attestation can record. */
export const chartField = z.enum(["medReviewed", "allergyReviewed", "historyReviewed"]);
export type ReviewItem =
  | { kind: "chart"; field: z.infer<typeof chartField>; reviewed: boolean }
  | { kind: "results"; result_ids: string[] };
/** The scope a review is built for: the same fields the write that follows it carries. */
export type ReviewScope = Pick<Scope, "patient_id" | "encounter_id" | "expected_revision">;

const header = { patient: patientRef, encounter: encounterRef };

export const noteReviewPayload = z
  .object({
    kind: z.literal("note_review"),
    ...header,
    /** The current draft: what Save replaces. There is no separately saved snapshot to diff against. */
    baseline: noteSchema,
    /** Exactly what Dobby proposed; sections it left out keep their current text. */
    proposed: noteSchema.partial(),
    saved: z.boolean(),
  })
  .strict();

const subject = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("medications"), medications: z.array(medicationRow) }).strict(),
  z.object({ kind: z.literal("allergy"), allergy: z.string(), reaction: z.string() }).strict(),
  z.object({ kind: z.literal("history"), visits: z.array(visitRow) }).strict(),
]);
const item = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("chart"),
      field: chartField,
      /** What the attestation will set: true = "I reviewed it", false = undo. */
      reviewed: z.boolean(),
      current: z.boolean(),
      subject,
    })
    .strict(),
  /** One attestation however many results it covers: the clinician confirms them together, in one write. */
  z.object({ kind: z.literal("results"), results: z.array(resultRow).min(1).max(5) }).strict(),
]);
export const reviewConfirmPayload = z
  .object({ kind: z.literal("review_confirm"), ...header, item })
  .strict();

function identity(store: Store, scope: ReviewScope) {
  const v = store.writable(scope);
  const p = getPatient(scope.patient_id);
  return {
    v,
    p,
    header: {
      patient: { patient_id: p.id, name: p.name, dob: p.dob },
      encounter: { id: v.id, revision: v.revision, signed: v.signed },
    },
  };
}

/** The note-review payload for `update_note`: nothing is written. Refuses a signed or stale encounter like the write would. */
export function buildNoteReview(store: Store, scope: ReviewScope, proposed: Partial<Note>) {
  const { v, header } = identity(store, scope);
  return noteReviewPayload.parse({ kind: "note_review", ...header, baseline: v.note, proposed, saved: v.saved });
}

const SUBJECT_OF = { medReviewed: "medications", allergyReviewed: "allergy", historyReviewed: "history" } as const;

/** The confirmation payload for `set_review_status` / `review_result`: nothing is written. */
export function buildReviewConfirm(store: Store, scope: ReviewScope, requested: ReviewItem) {
  const { v, p, header } = identity(store, scope);
  if (requested.kind === "results") {
    const all = resultsFor(p.id);
    const results = resultIndexes(p.id, requested.result_ids).map((i) => ({ ...all[i], reviewed: v.resultReviewed.includes(i) }));
    return reviewConfirmPayload.parse({ kind: "review_confirm", ...header, item: { kind: "results", results } });
  }
  const kind = SUBJECT_OF[requested.field];
  const shown =
    kind === "medications"
      ? { kind, medications: medicationsFor(p.id, v) }
      : kind === "allergy"
        ? { kind, allergy: p.allergy, reaction: p.reaction }
        : { kind, visits: historyFor(p.id, v) };
  return reviewConfirmPayload.parse({
    kind: "review_confirm",
    ...header,
    item: { kind: "chart", field: requested.field, reviewed: requested.reviewed, current: v[requested.field], subject: shown },
  });
}
